import { useId, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { AdminDrawer } from "@admin/_components/AdminDrawer";
import { Alert } from "@common/Alert";
import { Button } from "@common/index";
import { ComponentColor } from "@common/types";
import {
    addLocationRefusal,
    NOT_ADDED_MESSAGE,
    type AddLocationDone,
} from "../_helpers/addLocation";
import {
    addLocationFormSchema,
    EMPTY_ADD_LOCATION_FORM,
    toCreateApprovedLocationRequest,
    type AddLocationFormValues,
} from "../_helpers/addLocationForm";
import { AddressFormFields } from "./AddressFormFields";
import { FormField, INPUT_CLASS } from "./FormField";

interface AddLocationDrawerProps {
    // The client the location is added to.
    organizationId: string;
    onClose: () => void;
    // Everything the page says: the 201, the client gone (the API's own
    // 404) or closed (409), or a failure that may have added the location.
    // Any other 4xx, which added nothing, stays here, with the admin's
    // details.
    onDone: (done: AddLocationDone) => void;
}

// C4 (API #427): an admin adds a location to a client and approves it in the
// same step, with the business certificate they reviewed. Built from the Edit
// Location drawer: the same address fields, certificate rules and footer.
export const AddLocationDrawer = ({
    organizationId,
    onClose,
    onDone,
}: AddLocationDrawerProps) => {
    const { createApprovedLocation } = useOrganizationApi();
    const runGuarded = useSubmitGuard();
    const formId = useId();
    const nameInputId = useId();
    const certificateInputId = useId();

    const [isSaving, setIsSaving] = useState(false);
    const [notAdded, setNotAdded] = useState(false);

    const methods = useForm<AddLocationFormValues>({
        resolver: zodResolver(addLocationFormSchema),
        defaultValues: EMPTY_ADD_LOCATION_FORM,
    });
    const {
        register,
        formState: { errors },
    } = methods;

    const add = (values: AddLocationFormValues) =>
        runGuarded(async () => {
            setNotAdded(false);
            setIsSaving(true);

            try {
                const result = await createApprovedLocation(
                    organizationId,
                    toCreateApprovedLocationRequest(values),
                );
                onDone({ kind: "added", result });
            } catch (error) {
                const kind = addLocationRefusal(error);
                if (kind === "not_added") {
                    setNotAdded(true);
                    return;
                }
                onDone({ kind });
            } finally {
                setIsSaving(false);
            }
        });

    return (
        <AdminDrawer
            title="New Location"
            onClose={onClose}
            isProcessing={isSaving}
            footer={
                <div className="flex items-center gap-4">
                    {/* Outside the form, so tied to it by `form`. A native
                        button: Common Button does not forward `form`. */}
                    <button
                        type="submit"
                        form={formId}
                        disabled={isSaving}
                        className="btn btn-primary"
                    >
                        {isSaving && (
                            <span className="loading loading-spinner" />
                        )}
                        {isSaving ? "Adding…" : "Add and Approve"}
                    </button>
                    {/* text-neutral: a ghost button on a light surface is
                        otherwise white on white (see the UI CLAUDE.md). */}
                    <Button
                        color={ComponentColor.Ghost}
                        className="text-neutral"
                        disabled={isSaving}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                </div>
            }
        >
            {notAdded && (
                <Alert color={ComponentColor.Error} className="mb-4">
                    {NOT_ADDED_MESSAGE}
                </Alert>
            )}

            <p className="mb-4 text-sm">
                Adding the location approves it with this certificate. A
                client that already pays is charged for it at once.
            </p>

            <FormProvider {...methods}>
                <form
                    id={formId}
                    noValidate
                    onSubmit={methods.handleSubmit((values) => add(values))}
                >
                    <fieldset
                        disabled={isSaving}
                        className="flex flex-col gap-4"
                    >
                        <FormField
                            id={nameInputId}
                            label="Name"
                            error={errors.name?.message}
                        >
                            <input
                                id={nameInputId}
                                type="text"
                                className={INPUT_CLASS}
                                {...register("name")}
                            />
                        </FormField>
                        <AddressFormFields />
                        <FormField
                            id={certificateInputId}
                            label="Resale Certificate"
                            error={errors.resaleCertificate?.message}
                        >
                            <input
                                id={certificateInputId}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="file-input file-input-bordered w-full bg-white text-neutral"
                                {...register("resaleCertificate")}
                            />
                            <p className="mt-1 text-xs text-neutral/60">
                                PDF, JPG, or PNG, up to 10 MB.
                            </p>
                        </FormField>
                    </fieldset>
                </form>
            </FormProvider>
        </AdminDrawer>
    );
};
